import type React from 'react';
import { Link } from '@heroui/react';
import { Stepper } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { StepStripItem } from './stepStripMeta';

export type StepTitleProps = {
    item: StepStripItem;
    /** The step's address when it is a way back (`links`); absent, no link. */
    href: string | undefined;
};

/**
 * A step's name: a link back when it has an address; muted when the step is
 * done or skipped without one (nothing to reach there); else, for the
 * current and upcoming steps, as the Stepper shows it.
 *
 * The muted title is marked `data-static`; `index.css` gives it the
 * Stepper's own inactive colour (`--stepper-inactive-fg`, the one upcoming
 * titles have), so the foreground titles behind the current step are the
 * links.
 */
export const StepTitle: React.FC<StepTitleProps> = ({ item, href }) => {
    const { t } = useTranslation('pages');
    const name = t(`onboarding.steps.${item.step}`);
    const testId = getNodeTestIdProp(
        `onboarding-step-strip-title-${item.step}`,
    );

    if (href !== undefined) {
        return (
            <Stepper.Title {...testId}>
                <Link
                    aria-label={t('onboarding.frame.backToStep', {
                        step: name,
                    })}
                    href={href}
                    {...getNodeTestIdProp(
                        `onboarding-step-strip-link-${item.step}`,
                    )}
                >
                    {name}
                </Link>
            </Stepper.Title>
        );
    }
    const isStatic = item.state === 'done' || item.state === 'skipped';
    return (
        <Stepper.Title data-static={isStatic || undefined} {...testId}>
            {name}
        </Stepper.Title>
    );
};
